import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { DEFAULT_CHOICE, choiceFor } from '@domain/notification'
import type {
  Notification,
  NotificationChoice,
  NotificationSettings,
  NotificationType,
} from '@domain/notification'
import { currentReferenceMonth } from '@shared/date/timezone'
import { Glass } from '@ui/glass/Glass'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import type { Recado } from '@domain/recado'
import { useReadyUser } from '@ui/auth/auth-context'
import { FolhaDeRecado } from '@ui/notifications/FolhaDeRecado'
import { RecadoPorInteiro } from '@ui/notifications/RecadoPorInteiro'
import { pedacosDe } from '@ui/notifications/links'
import { Botao } from '@ui/glass/Botao'
import { BotaoDeIcone } from '@ui/shell/BotaoDeIcone'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { IconeChequeDuplo, IconeLixeira, IconeMais } from '@ui/shell/icones'
import { PushSection } from '@ui/notifications/PushSection'
import { usePush } from '@ui/notifications/use-push'

/**
 * A central de notificações. Fase 6.
 *
 * ## Cada linha leva a algum lugar
 *
 * `Docs/02-Dominio/Notification.md`: *"Notificação que não navega para lugar
 * nenhum é ruído, e ruído treina o usuário a ignorar as próximas."* A rota vem
 * de `data.rota`, que o `NotificationService` escreveu — a tela não deduz
 * destino a partir do tipo, senão haveria dois lugares decidindo para onde uma
 * notificação leva.
 *
 * ## Ler é um efeito de ABRIR, não um botão escondido
 *
 * Tocar na notificação marca como lida **e** navega. Um "marcar como lida"
 * separado em cada linha faria a pessoa fazer duas vezes o trabalho que o
 * sistema já sabe: ela abriu, ela leu.
 *
 * "Marcar todas" existe à parte porque é a outra intenção — a de quem não vai
 * abrir nenhuma.
 *
 * ## As preferências ficam AQUI, e não em Ajustes
 *
 * É onde a pessoa está quando pensa "isso aqui é demais". Mandá-la a outra tela
 * para desligar um tipo é o atrito que faz ela desligar as notificações do
 * sistema operacional inteiro — que é irreversível na prática, e leva junto os
 * tipos que ela queria.
 */

const TYPE_LABELS: Record<NotificationType, string> = {
  'sync.summary': 'Resumo da sincronização',
  'sync.failed': 'Falha na sincronização',
  'sync.stale': 'Transações paradas',
  'invoice.due_soon': 'Fatura vencendo',
  'transaction.created': 'Transação nova',
  'transaction.large_amount': 'Compra de valor alto',
  'invoice.closed': 'Fatura fechada',
  'receipt.awaiting_review': 'Nota fiscal aguardando revisão',
  'product.price_drop': 'Queda de preço',
  'reminder.due': 'Lembrete',
}

/**
 * Os tipos que **têm produtor hoje**. Os outros existem no catálogo e ninguém os
 * emite — oferecer preferência para eles seria um controle que não controla
 * nada, e a pessoa descobriria isso do pior jeito: silenciando e continuando a
 * não receber, sem saber que nunca ia receber.
 */
const COM_PRODUTOR: NotificationType[] = [
  'sync.summary',
  'sync.failed',
  'sync.stale',
  'invoice.due_soon',
]

const CHOICE_LABELS: Record<NotificationChoice, string> = {
  push: 'Push e no app',
  inApp: 'Só no app',
  off: 'Silenciado',
}

/** `'2026-09-01T06:00:00.000Z'` → `'01/09 06:00'`. Ano só quando difere. */
function quando(iso: string, anoCorrente: string): string {
  const ano = iso.slice(0, 4)
  const base = `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}`
  return ano === anoCorrente ? base : `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${ano} ${iso.slice(11, 16)}`
}

function Linha({
  notification,
  anoCorrente,
  onOpen,
}: {
  notification: Notification
  anoCorrente: string
  onOpen: () => void
}) {
  const naoLida = notification.readAt === null
  const rota = notification.data['rota'] ?? '/'

  return (
    <li className="border-b border-[var(--border)] py-2">
      <Link
        to={rota}
        data-nav-linha
        className="block"
        onClick={() => {
          // Marca como lida ao abrir. Ver o cabeçalho.
          if (naoLida) onOpen()
        }}
      >
        <p className={`text-sm ${naoLida ? 'font-semibold' : ''}`}>
          {/* O ponto de não lida é textual além de visual: um marcador só de
              cor não chega a quem usa leitor de tela. */}
          {naoLida && <span className="sr-only">Não lida. </span>}
          {notification.title}
        </p>
        <p className="text-sm text-[var(--text-muted)]">{notification.body}</p>
        <p className="text-xs text-[var(--text-muted)]">
          {quando(notification.createdAt, anoCorrente)}
        </p>
      </Link>
    </li>
  )
}

/**
 * Um recado na mesma lista.
 *
 * ## Ele não navega, e por isso não é um link
 *
 * Toda notificação leva a algum lugar — *"notificação que não navega para lugar
 * nenhum é ruído"*. Um recado é o destino: o conteúdo dele já é o que se veio
 * ler. Fingi-lo clicável levaria a lugar nenhum, que é o defeito que aquela
 * regra existe para evitar.
 *
 * ## A etiqueta do autor vem antes do texto
 *
 * "Um agente de IA" e "Murilo" pedem confiança diferente do mesmo parágrafo, e
 * quem lê precisa saber disso **antes** de ler — não depois.
 */
function LinhaDeRecado({
  recado,
  anoCorrente,
  onAbrir,
}: {
  recado: Recado
  anoCorrente: string
  onAbrir: () => void
}) {
  return (
    <li className="border-b border-[var(--border)]">
      <button
        type="button"
        onClick={onAbrir}
        aria-label={`Abrir o recado de ${recado.autor.label}`}
        data-nav-linha
        className="alvo w-full flex-col items-start gap-0 py-2 text-left"
      >
        <span className="flex w-full items-baseline gap-2">
          <span className="text-xs" style={{ color: 'var(--economia)' }}>
            {recado.autor.label}
          </span>
          <span className="flex-1 text-xs text-[var(--text-muted)]">
            {quando(recado.createdAt, anoCorrente)}
          </span>
        </span>
        {/* Duas linhas de prévia. O primeiro recado escrito de verdade tinha 932
            caracteres — inteiro aqui, ele empurrava sete notificações para fora
            da tela. `line-clamp-2` corta por LINHA renderizada, e não por
            contagem de caracteres, que erraria com uma linha curta seguida de
            uma URL de duzentos.

            O endereço vira o rótulo curto, e NÃO vira `<a>`: a linha inteira já
            é um `<button>`, e âncora dentro de botão é HTML inválido — além de
            ser dois alvos disputando o mesmo dedo. Clicável é na folha. */}
        <span className="line-clamp-2 w-full text-sm">{semEnderecos(recado.texto)}</span>
      </button>
    </li>
  )
}

type FeedItem =
  | { tipo: 'notificacao'; notificacao: Notification }
  | { tipo: 'recado'; recado: Recado }

/**
 * A data de ordenação.
 *
 * `createdAt` vazio quer dizer **acabou de nascer**: o `serverTimestamp()` ainda
 * não voltou do servidor, e o adapter não inventa o relógio do host. Ordenado
 * como string vazia ele iria para o fim da lista — o recado recém-escrito
 * sumindo lá embaixo é o oposto do que se espera. O `\uffff` o põe no topo até
 * o servidor responder.
 */
/**
 * O texto da prévia com os endereços trocados pelo rótulo curto.
 *
 * Quatro links de Maps de 200 caracteres cada não deixavam sobrar espaço para o
 * nome da loja nas duas linhas — que é justamente o que se lê de relance.
 */
function semEnderecos(texto: string): string {
  return pedacosDe(texto)
    .map((p) => (p.tipo === 'link' ? p.rotulo : p.texto))
    .join('')
}

function quandoDe(item: FeedItem): string {
  const bruto = item.tipo === 'recado' ? item.recado.createdAt : item.notificacao.createdAt
  return bruto === '' ? '\uffff' : bruto
}

export function Notificacoes() {
  const { notificationRepository, recadoRepository } = useFinanceRepositories()
  const usuario = useReadyUser()
  const [recados, setRecados] = useState<Recado[]>([])
  const [escrevendo, setEscrevendo] = useState(false)
  const [aberto, setAberto] = useState<Recado | null>(null)
  const [confirmandoLimpeza, setConfirmandoLimpeza] = useState(false)
  const { facts, subscribe } = usePush()
  const [lista, setLista] = useState<Notification[] | 'loading' | 'failed'>('loading')
  const [settings, setSettings] = useState<NotificationSettings | null>(null)
  const anoCorrente = currentReferenceMonth().slice(0, 4)

  useEffect(() => {
    let live = true
    // As duas leituras juntas, e o `setState` só no `.then` — a mesma forma de
    // `use-monthly-book.ts`. Chamar um `useCallback` que já faz `setState` faz
    // o `react-hooks/set-state-in-effect` enxergar o caminho síncrono.
    void Promise.all([
      notificationRepository.listMine(),
      notificationRepository.readSettings(),
      recadoRepository.list(),
    ])
      .then(([notificacoes, preferencias, deixados]) => {
        if (!live) return
        setLista(notificacoes)
        setSettings(preferencias)
        setRecados(deixados)
      })
      .catch((error: unknown) => {
        console.error('Notificacoes: a leitura falhou', error)
        if (live) setLista('failed')
      })
    return () => {
      live = false
    }
  }, [notificationRepository, recadoRepository])

  const marcarLida = (id: string) => {
    // Otimista, e aqui é o lugar certo para isso: a pessoa está navegando para
    // fora da tela, e esperar a escrita para mudar um negrito que ela não vai
    // mais ver seria segurar a navegação por nada.
    setLista((atual) =>
      Array.isArray(atual)
        ? atual.map((n) => (n.id === id ? { ...n, readAt: 'agora' } : n))
        : atual,
    )
    void notificationRepository.markRead(id).catch((error: unknown) => {
      console.error('Notificacoes: marcar como lida falhou', error)
    })
  }

  const escolher = (type: NotificationType, choice: NotificationChoice) => {
    const novas: NotificationSettings = {
      byType: { ...(settings?.byType ?? {}), [type]: choice },
    }
    setSettings(novas)
    void notificationRepository.saveSettings(novas).catch((error: unknown) => {
      console.error('Notificacoes: salvar preferências falhou', error)
    })
  }

  const apagarRecado = (id: string) => {
    // Otimista: apagar é o gesto de quem já decidiu, e segurar a lista por uma
    // escrita que quase sempre dá certo faria a linha ficar ali piscando.
    setRecados((atual) => atual.filter((r) => r.id !== id))
    void recadoRepository.remove(id).catch((error: unknown) => {
      console.error('Notificacoes: apagar o recado falhou', error)
    })
  }

  const naoLidas = Array.isArray(lista) ? lista.filter((n) => n.readAt === null) : []

  /**
   * As duas coleções numa lista só, **por data**.
   *
   * Elas são coisas diferentes no banco — uma tem destinatário e entrega, a
   * outra é da casa e não notifica ninguém — e a mesma coisa para quem lê:
   * o que aconteceu, em ordem. Juntar na tela é mais barato que forçar as duas
   * a caberem num modelo só, e é reversível.
   */
  const feed: FeedItem[] = [
    ...(Array.isArray(lista) ? lista.map((n) => ({ tipo: 'notificacao' as const, notificacao: n })) : []),
    ...recados.map((r) => ({ tipo: 'recado' as const, recado: r })),
  ].sort((a, b) => quandoDe(b).localeCompare(quandoDe(a)))

  return (
    <section>
      {/* **Uma fileira só, e o "+" por último.** Os quatro filhos viviam numa
          `flex-wrap` com `justify-between`: bastava o texto "Marcar todas como
          lidas (1)" para "Apagar todas" descer sozinho para a linha de baixo, e
          o "+" ficava espremido no meio, em lugar diferente conforme o que
          houvesse para fazer. Visto em produção. Agora o título encolhe e as
          ações ficam no canto direito, na mesma ordem sempre — o mesmo arranjo
          dos Lembretes (decisão 276) e de Transações.

          **As duas ações viraram ícones** porque em texto elas empurravam o
          "+": três alvos de 44px cabem na fileira, três frases não. O que
          contava — quantas estão por ler — virou o número em cima do visto, e
          continua no rótulo lido em voz alta. */}
      <header className="mb-4 flex items-center justify-between gap-2">
        <h1 className="titulo min-w-0 truncate text-2xl">Notificações</h1>
        <div className="fileira-de-icones shrink-0">
          {naoLidas.length > 0 && (
            <BotaoDeIcone
              rotulo={`Marcar todas como lidas (${String(naoLidas.length)})`}
              onClick={() => {
                const ids = naoLidas.map((n) => n.id)
                setLista((atual) =>
                  Array.isArray(atual) ? atual.map((n) => ({ ...n, readAt: n.readAt ?? 'agora' })) : atual,
                )
                void notificationRepository.markAllRead(ids).catch((error: unknown) => {
                  console.error('Notificacoes: marcar todas falhou', error)
                })
              }}
            >
              <IconeChequeDuplo />
              {/* `aria-hidden`: o rótulo do botão já diz o número, e anunciá-lo
                  duas vezes seria ler "1" depois de "…como lidas (1)". */}
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-semibold text-[var(--bg)]"
                style={{ background: 'var(--brand)' }}
              >
                {naoLidas.length}
              </span>
            </BotaoDeIcone>
          )}
          {Array.isArray(lista) && lista.length > 0 && (
            <BotaoDeIcone
              rotulo="Apagar todas"
              onClick={() => {
                setConfirmandoLimpeza(true)
              }}
            >
              <IconeLixeira />
            </BotaoDeIcone>
          )}
          {/* Sempre o último, e sempre no canto: é a única ação que não depende
              de haver aviso nenhum. */}
          <BotaoDeIcone
            rotulo="Novo recado"
            onClick={() => {
              setEscrevendo(true)
            }}
          >
            <IconeMais />
          </BotaoDeIcone>
        </div>
      </header>

      {/* **Confirma, e o número vai na pergunta.** Apagar treze avisos não tem
          desfazer, e "tem certeza?" sem dizer quantos é uma pergunta que não dá
          o que é preciso para responder. Folha e não `window.confirm`, pela
          decisão 245e. E ela apaga só as NOTIFICAÇÕES: os recados ficam, porque
          são outra coleção e outro tipo de coisa — o que se joga fora aqui é o
          que o sistema avisou, não o que alguém escreveu. */}
      {confirmandoLimpeza && Array.isArray(lista) && (
        <FolhaDeBaixo
          titulo="Apagar todas as notificações"
          onFechar={() => {
            setConfirmandoLimpeza(false)
          }}
        >
          <h2 className="titulo mb-2 text-base">Apagar todas?</h2>
          <p className="mb-4 text-sm text-[var(--text-muted)]">
            {lista.length === 1
              ? 'A notificação some para sempre. Os recados ficam.'
              : `As ${String(lista.length)} notificações somem para sempre. Os recados ficam.`}
          </p>
          <div className="flex flex-wrap gap-2">
            <Botao
              type="button"
              tom="perigo"
              onClick={() => {
                const ids = lista.map((n) => n.id)
                setLista([])
                setConfirmandoLimpeza(false)
                void notificationRepository.removeAll(ids).catch((error: unknown) => {
                  console.error('Notificacoes: apagar todas falhou', error)
                })
              }}
            >
              {lista.length === 1 ? 'Apagar a notificação' : `Apagar as ${String(lista.length)}`}
            </Botao>
            <Botao
              type="button"
              onClick={() => {
                setConfirmandoLimpeza(false)
              }}
            >
              Cancelar
            </Botao>
          </div>
        </FolhaDeBaixo>
      )}

      {aberto !== null && (
        <RecadoPorInteiro
          recado={aberto}
          quando={quando(aberto.createdAt, anoCorrente)}
          onApagar={() => {
            apagarRecado(aberto.id)
          }}
          onFechar={() => {
            setAberto(null)
          }}
        />
      )}

      {escrevendo && (
        <FolhaDeRecado
          doUsuario={usuario.displayName}
          onFechar={() => {
            setEscrevendo(false)
          }}
          onGravar={async (novo) => {
            const id = await recadoRepository.create(novo)
            // Relê em vez de inserir na mão: `createdAt` e `createdBy` são
            // carimbados pelo servidor, e inventá-los aqui daria uma linha que
            // muda de lugar na lista assim que alguém recarregar.
            void id
            setRecados(await recadoRepository.list())
          }}
        />
      )}

      <Glass level="frost" className="mb-4 p-5">
        {lista === 'loading' && (
          <p role="status" className="text-sm text-[var(--text-muted)]">
            Carregando…
          </p>
        )}
        {lista === 'failed' && (
          <p role="alert" className="text-sm text-[var(--text-muted)]">
            Não foi possível carregar as notificações agora.
          </p>
        )}
        {Array.isArray(lista) &&
          (feed.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">
              Nada por aqui. Você é avisado quando a sincronização traz novidade ou falha — e dá
              para deixar um recado no “+” acima.
            </p>
          ) : (
            <ul aria-label="Notificações">
              {feed.map((item) =>
                item.tipo === 'recado' ? (
                  <LinhaDeRecado
                    key={`r-${item.recado.id}`}
                    recado={item.recado}
                    anoCorrente={anoCorrente}
                    onAbrir={() => {
                      setAberto(item.recado)
                    }}
                  />
                ) : (
                  <Linha
                    key={`n-${item.notificacao.id}`}
                    notification={item.notificacao}
                    anoCorrente={anoCorrente}
                    onOpen={() => {
                      marcarLida(item.notificacao.id)
                    }}
                  />
                ),
              )}
            </ul>
          ))}
      </Glass>

      <Glass level="frost" className="p-5">
        <h2 className="mb-3 text-xs text-[var(--text-muted)]">O que você quer receber</h2>
        <ul>
          {COM_PRODUTOR.map((type) => (
            <li key={type} className="flex flex-wrap items-center gap-2 border-b border-[var(--border)] py-2 text-sm">
              <span className="flex-1">{TYPE_LABELS[type]}</span>
              <select
                aria-label={TYPE_LABELS[type]}
                value={choiceFor(settings, type)}
                className="rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
                onChange={(event) => {
                  escolher(type, event.target.value as NotificationChoice)
                }}
              >
                {(Object.keys(CHOICE_LABELS) as NotificationChoice[]).map((choice) => (
                  <option key={choice} value={choice}>
                    {CHOICE_LABELS[choice]}
                  </option>
                ))}
              </select>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-[var(--text-muted)]">
          {`Padrão de cada tipo: ${COM_PRODUTOR.map(
            (t) => `${TYPE_LABELS[t]} — ${CHOICE_LABELS[DEFAULT_CHOICE[t]]}`,
          ).join(' · ')}.`}
        </p>

        {/* **Embaixo da lista, e não no topo.** A permissão é pedida em
            contexto: quando a pessoa chega aqui, ela já viu o que existe para
            receber. Pedir cedo é o caminho mais curto para a negativa, que é
            praticamente irreversível. */}
        <PushSection facts={facts} onSubscribe={subscribe} />
      </Glass>
    </section>
  )
}
